import { Injectable } from '@angular/core';
import {
  HTTP_INTERCEPTORS,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpClient
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, take, switchMap, finalize, timeout } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { Router } from '@angular/router';
import { IError } from 'src/app/models/error.model';
import { Store } from '@ngrx/store';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { GlobalConfigService } from '../global-config.service';
import { ContextService } from '../context.service';
import { SessionConflictService } from '../session-conflict.service';

const TOKEN_HEADER_KEY = 'Authorization';

interface RefreshTokenResponse {
  access_token?: string;
  jwt_token?: string;
  refresh_token?: string;
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private apiUrl: string;

  constructor(
    private authService: AuthService,
    private router: Router,
    private store: Store<{ warningPopup: IError }>,
    private globalConfig: GlobalConfigService,
    private http: HttpClient,
    private contextService: ContextService,
    private sessionConflictService: SessionConflictService
  ) {
    this.apiUrl = this.globalConfig.getApiUrl();
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Contexto da rota atual e do endpoint para tratar fluxos públicos x privados
    const currentUrl = this.router.url || '';
    const isPublicRoute =
      /^(\/)?$/.test(currentUrl) ||
      currentUrl.startsWith('/login') ||
      currentUrl.startsWith('/forgot-password') ||
      currentUrl.startsWith('/verify-code') ||
      currentUrl.startsWith('/anamneses/') ||
      currentUrl.startsWith('/formularios/') ||
      currentUrl.startsWith('/anamnese-invalid') ||
      currentUrl.startsWith('/anamnese-success');

    const isPublicAnamneseApi = !!req.url && req.url.includes(`${this.apiUrl}/documents/anamnese/public`);
    const isRefreshRequest = req.url.includes('/auth/refresh');
    
    // Endpoints de autenticação que NÃO devem ter token adicionado e NÃO devem tentar refresh
    const isAuthEndpoint = req.url.includes('/users/login') || 
                           req.url.includes('/auth/refresh') ||
                           req.url.includes('/auth/forgot-password') ||
                           req.url.includes('/auth/reset-password') ||
                           req.url.includes('/auth/verify-code') ||
                           req.url.includes('/users/auth/');

    // Verifica se a requisição é para o ViaCEP ou outros serviços externos
    const isExternalService = !req.url.includes(this.apiUrl);

    // Clona a requisição com os headers apropriados
    const authReq = this.addTokenToRequest(req, isExternalService, isPublicAnamneseApi, isAuthEndpoint);

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // Se for erro 401 e não for requisição de auth/refresh/pública
        if (error.status === 401 && !isRefreshRequest && !isAuthEndpoint && !isPublicRoute && !isPublicAnamneseApi && !isExternalService) {
          return this.handle401Error(req, next);
        }

        // Tratamento de outros erros
        this.handleOtherErrors(error, req);
        
        return throwError(() => error);
      })
    );
  }

  private addTokenToRequest(req: HttpRequest<any>, isExternalService: boolean, isPublicAnamneseApi: boolean, isAuthEndpoint: boolean): HttpRequest<any> {
    if (isExternalService) {
      return req.clone();
    }

    let headers = req.headers;

    // Em endpoints públicos de anamnese ou endpoints de auth, sair antes de
    // injetar QUALQUER header customizado (Authorization OU X-Active-Clinic).
    // X-Active-Clinic não tem dono lógico em rota pública (paciente respondendo
    // anamnese / paciente subindo foto via QR), e cada header customizado
    // adicional aumenta a superfície de OPTIONS preflight em mobile — onde
    // observamos preflights travando intermitente atrás do Cloudflare.
    if (isPublicAnamneseApi || isAuthEndpoint) {
      return req.clone({ headers });
    }

    // Rota autenticada: injeta X-Active-Clinic se houver clínica ativa no contexto
    const activeClinic = this.contextService.getActiveClinicSnapshot();
    if (activeClinic?.cnpj) {
      headers = headers.set('X-Active-Clinic', activeClinic.cnpj);
    }

    const token = this.authService.getToken();
    if (token) {
      headers = headers.set(TOKEN_HEADER_KEY, 'Bearer ' + token);
    }

    return req.clone({ headers });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Se já estiver fazendo refresh, aguarda na fila
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          // Refaz a requisição original com o novo token
          return next.handle(this.addNewTokenToRequest(request, token!));
        })
      );
    }

    // Inicia o processo de refresh
    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    const email = this.authService.getUserEmail();
    const accessToken = this.authService.getToken();
    const refreshToken = this.authService.getRefreshToken();

    // Se não tiver refresh token, redireciona para login
    if (!refreshToken || !email) {
      return this.logoutAndRedirect();
    }

    const refreshUrl = `${this.apiUrl}/auth/refresh`;
    const payload = {
      email: email,
      access_token: accessToken,
      refresh_token: refreshToken
    };

    return this.http.post<RefreshTokenResponse>(refreshUrl, payload).pipe(
      timeout(15000),
      switchMap((response: RefreshTokenResponse) => {
        const newToken = response.access_token || response.jwt_token || '';
        
        if (newToken) {
          // Salva o novo token
          this.authService.setToken(newToken);
          
          // Salva o novo refresh token se vier na resposta
          if (response.refresh_token) {
            this.authService.setRefreshToken(response.refresh_token);
          }

          // Notifica as requisições em espera que o token foi atualizado
          this.refreshTokenSubject.next(newToken);

          // Refaz a requisição original com o novo token
          return next.handle(this.addNewTokenToRequest(request, newToken));
        }

        // Se não veio token na resposta, faz logout
        return this.logoutAndRedirect();
      }),
      catchError((refreshError) => {
        // Se o refresh falhar, faz logout
        console.error('Erro ao fazer refresh do token:', refreshError);
        return this.logoutAndRedirect();
      }),
      finalize(() => {
        this.isRefreshing = false;
      })
    );
  }

  private addNewTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    let headers = request.headers
      .set(TOKEN_HEADER_KEY, 'Bearer ' + token)
      .set("ngrok-skip-browser-warning", "True")
      .set("Access-Control-Allow-Origin", "*");

    // Re-injeta X-Active-Clinic no retry após token refresh
    const activeClinic = this.contextService.getActiveClinicSnapshot();
    if (activeClinic?.cnpj) {
      headers = headers.set('X-Active-Clinic', activeClinic.cnpj);
    }

    return request.clone({ headers });
  }

  private logoutAndRedirect(): Observable<never> {
    this.isRefreshing = false;
    this.authService.clear();
    this.router.navigate(['/login'], { queryParams: { sessionExpired: 'true' } });
    return throwError(() => new Error('Session expired'));
  }

  private handleOtherErrors(err: HttpErrorResponse, req: HttpRequest<any>): void {
    // Suppress noisy 400 from clinic questions endpoint: when the clinic has no specific
    // questions the backend may return a 400 used as a control flow signal. We don't want
    // to show a global popup for that case because the UI handles the fallback gracefully.
    if (err.status === 400 && req.url.includes('/incidents/questions')) {
      console.warn('[AuthInterceptor] Suppressed clinic questions 400:', req.url, err.error?.message || err.message);
      return;
    }
    // Suppress 404 from incident-report GET: ausência de relatório é estado normal
    // (plano sem intercorrência ainda). O service já resolve [] e o UI renderiza
    // o formulário em branco — não mostrar popup global.
    if (err.status === 404 && req.method === 'GET' && /\/treatment-plans\/[^/]+\/incident-report(\/|$|\?)/.test(req.url)) {
      console.warn('[AuthInterceptor] Suppressed incident-report 404 (no report yet):', req.url);
      return;
    }
    if (err.status === 409 && err.error?.error === 'SESSION_CONFLICT') {
      const message = err.error?.message || 'Sessão encerrada. Um novo login foi detectado em outro dispositivo.';
      this.sessionConflictService.triggerConflict(message);
      return;
    }

    const body = err.error;
    if (body) {
      const error = body as IError;
      if (error && error.status) {
        this.store.dispatch(setWarningText(error.message, error.status));
      }
    }
  }
}

export const authInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }
];